import React, { PropsWithChildren, useEffect, useState } from 'react';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import {
  getTimeToEvent,
  NO_TIME_LEFT_TO_EVENT,
  TimeToEvent,
} from '../../helpers/dateHelpers';
import { applyComponentTheme } from '../../theme';
import {
  ClockItem,
  ClockItemsContainer,
  CountdownClockContainer,
  Heading,
  Label,
  NumberBox,
} from './styles';

export type CountdownClockProps = {
  eventUpcomingHeading: string;
  eventInProgressHeading: string;
  eventFinishedHeading: string;
  eventStartDate: Date;
  eventEndDate: Date;
} & ComponentProps;

const CountdownClock: React.FC<PropsWithChildren<CountdownClockProps>> = ({
  eventUpcomingHeading,
  eventInProgressHeading,
  eventFinishedHeading,
  eventStartDate,
  eventEndDate,
  ...props
}) => {
  // bit oscillates between true and false every second causing the component to re-render
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [bit, setBit] = useState<boolean>(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setBit((previous) => !previous);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  let heading: string;
  let timeToEvent: TimeToEvent;

  const currentTimestamp = new Date().getTime();

  if (currentTimestamp < eventStartDate.getTime()) {
    heading = eventUpcomingHeading;
    timeToEvent = getTimeToEvent(eventStartDate);
  } else if (currentTimestamp < eventEndDate.getTime()) {
    heading = eventInProgressHeading;
    timeToEvent = NO_TIME_LEFT_TO_EVENT;
  } else {
    heading = eventFinishedHeading;
    timeToEvent = NO_TIME_LEFT_TO_EVENT;
  }

  return (
    <CountdownClockContainer tabIndex={0} {...componentProps(props)}>
      <Heading>{heading}</Heading>
      <ClockItemsContainer tabIndex={0}>
        <ClockItem>
          <NumberBox data-testid="days-left">{timeToEvent.days}</NumberBox>
          <Label>Days</Label>
        </ClockItem>
        <ClockItem>
          <NumberBox data-testid="hours-left">{timeToEvent.hours}</NumberBox>
          <Label>Hours</Label>
        </ClockItem>
        <ClockItem>
          <NumberBox data-testid="minutes-left">{timeToEvent.minutes}</NumberBox>
          <Label>Minutes</Label>
        </ClockItem>
        <ClockItem>
          <NumberBox data-testid="seconds-left">{timeToEvent.seconds}</NumberBox>
          <Label>Seconds</Label>
        </ClockItem>
      </ClockItemsContainer>
    </CountdownClockContainer>
  );
};

export default applyComponentTheme(CountdownClock, 'CountdownClock');
